import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
} from "react";
import { Checkbox, Col, Row, Modal, InputNumber, Select } from "antd";
import { debounce } from "lodash";

//Helpers
import {
  EDIT_TRADE_DETAILS_LABELS,
  STATE_KEYS_NAME,
} from "constants/auditTrail";

import {
  handleCopyTradeDetails,
  setDataToLocalStorage,
  toast,
} from "helpers/generalHelper";
//Components
// Context
import { AuditTrailContext } from "contexts/AuditTrailContext";
// Style
import "./EditTradeDetails.scss";
import { GlobalContext } from "contexts/GlobalContext";
import {
  getEditEditTRadeDetails,
  storeOrUpdateTradeDetails,
  updateLiveTrade,
  updateSummaryTrade,
  updateTradeLegs,
} from "api/editTradeDetailsApi";
import useDebounce from "hooks/useDebounce";
import useDraggableModal from "hooks/useDraggableModal";
import EditTradePremium from "./EditTradePremium";
import BuyOrSellButtons from "./BuyOrSellButtons";
import { EditTradeFooter } from "./EditTradeFooter";
import { API_KEYS, BACKEND_KEYS_NAME } from "constants/editTradeDetails";
import PreLoadPagesSpin from "components/PreLoadPagesSpin";
import { returnSelectedIdForEditTradeDetails } from "helpers/editTradeDetailsHelper";
import AddTraderForm from "components/TeamRun/AddTraderForm";
import { useLocation } from "react-router-dom";
import { ROUTES_PATH } from "routes";
import TradeColItem from "./TradeColItem";

const EditTradeDetails = ({ isEditMode = false, ...props }) => {
  const location = useLocation();
  const { setOnSaveAndCreateClicked } = useContext(AuditTrailContext);
  const {
    globalState: { dark_Theme },
  } = useContext(GlobalContext);
  const { modalRender, title: draggableTitle } = useDraggableModal(
    props.isOpen,
    EDIT_TRADE_DETAILS_LABELS.TRADE_OPTION
  );

  //#States
  const [isSellActive, setIsSellActive] = useState(null);
  const [isReverse, setIsReverse] = useState(true);
  const [isTradeRequestFire, setIsTradeRequestFire] = useState(null);
  const [tradeData, setTradeData] = useState();
  const [oldTradeData, setOldTradeData] = useState({
    leg_premium: 0,
    leg_volatility: 0,
    traded_premium: 0,
    traded_vol: 0,
    quantity_t: 0,
    delta_quantity_t: 0,
    delta: 0,
    leg_quantity_t: {},
  });
  const [isAddTraderOpen, setIsAddTraderOpen] = useState(false);
  // const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [exChangeData, setExChangeData] = useState([]);
  const [tradingCompanyId, setTradingCompanyId] = useState();
  const [isLive, setIsLive] = useState(false);
  const [isLiveRequestActive, setIsLiveRequestActive] = useState(false);
  const [isCreateLoading, setIsCreateLoading] = useState(false);
  const [isRequestActive, setIsRequestActive] = useState(false);
  const [isPremiumRequestActive, setIsPremiumRequestActive] = useState(false);
  const [isQuantityRequestActive, setIsQuantityRequestActive] = useState(false);
  const [isDeltaRequestActive, setIsDeltaRequestActive] = useState(false);
  const [isLegPremiumRequestActive, setIsLegPremiumRequestActive] =
    useState(false);

  const [editTradeValue, setEditTradeValue] = useState(undefined);
  const [editVolatilityValue, setEditVolatilityValue] = useState(undefined);
  const [editQuantityValue, setEditQuantityValue] = useState(undefined);
  const debouncedTradePremiumValue = useDebounce(editTradeValue, 1000);
  const debouncedTradeVolatilityValue = useDebounce(editVolatilityValue, 1000);
  const [currentCol, setCurrentCol] = useState(null);

  // use effect on isSellActive change
  useEffect(() => {
    setEditQuantityValue(undefined);
  }, [isSellActive]);

  useEffect(() => {
    if (debouncedTradePremiumValue != null) {
      handleTradeSummary("premium", debouncedTradePremiumValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTradePremiumValue]);

  useEffect(() => {
    if (debouncedTradeVolatilityValue != null) {
      handleTradeSummary("vol", debouncedTradeVolatilityValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedTradeVolatilityValue]);

  useEffect(() => {
    if (isTradeRequestFire && tradeData?.data?.sell_or_buy === null) {
      getTradeDetailsData();
    } else if (isSellActive === null) {
      getTradeDetailsData(true);
    } else {
      getTradeDetailsData(false, false, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTradeRequestFire, isSellActive]);

  useEffect(() => {
    if (tradeData?.data?.trades) {
      tradeData.data.trades?.forEach((item, index) => {
        item.hedges.forEach((hedge, hedgeIndex) => {
          hedge.col_index = index;
          hedge.hedge_col = hedgeIndex;
        });
        item.legs.at(0).leg_col = index;
      });
    }
  }, [tradeData?.data?.trades]);

  function getTradeDetailsData(
    isFirst = false,
    withLoading = true,
    resetData = true
  ) {
    let bodyParams = {
      team_run_row_id: props.rowSelectedInfo?.["team_run_row_id"],
    };
    if (isFirst) {
      bodyParams["summary_id"] = props.rowSelectedInfo?.["summary_id"];
      bodyParams["is_sell"] = 0;
      bodyParams["show_null_values"] = 1;
    } else {
      bodyParams["temp_summary_id"] = tradeData?.data?.["temp_summary_id"];
      bodyParams["is_sell"] = isSellActive ? 1 : 0;
    }
    setIsRequestActive(true);
    if (withLoading) {
      setIsLoading(true);
      setTradeData({});
    }
    getEditEditTRadeDetails(bodyParams)
      .then((res) => {
        // setIsReverse(true);
        setDataToLocalStorage(
          "initial_hedges_sell_or_buy",
          res?.data?.data?.left.hedges.map((hed) => {
            return {
              id: hed.id,
              sell_or_buy: hed.sell_or_buy,
            };
          })
        );
        if (resetData) {
          setTradeData(res?.data);
          setIsLive(Boolean(res?.data?.data?.[BACKEND_KEYS_NAME.IS_LIVE]));
          setIsLoading(false);
          setExChangeData(
            res.data?.data?.left?.["exchanges"].map((item) => ({
              ...item,
              label: item[BACKEND_KEYS_NAME.NAME],
              value: item[BACKEND_KEYS_NAME.ID],
            }))
          );
        } else {
          setTradeData((prev) => {
            return {
              ...prev,
              data: {
                ...prev.data,
                left: {
                  ...prev.data.left,
                  is_sell: res?.data?.data?.left?.is_sell,
                  legs: prev.data.left?.legs.map((item) => {
                    return {
                      ...item,
                      sell_or_buy: res?.data?.data?.left?.legs?.find(
                        (leg) => leg.id === item.id
                      )?.sell_or_buy,
                    };
                  }),
                  hedges: prev.data.left?.hedges.map((item) => {
                    const hedgeSellOrBuy = res?.data?.data?.left?.hedges?.find(
                      (hedge) => hedge.id === item.id
                    )?.sell_or_buy;
                    return {
                      ...item,
                      sell_or_buy:
                        item.sell_or_buy !== hedgeSellOrBuy
                          ? hedgeSellOrBuy
                          : item.sell_or_buy === "buy"
                          ? "sell"
                          : "buy",
                    };
                  }),
                },
                trades: prev.data.trades.map((item) => {
                  return {
                    ...item,
                    legs: item.legs,
                    hedges: item.hedges.map((hed) => {
                      const hedgeSellOrBuy =
                        res?.data?.data?.left?.hedges?.find(
                          (hedge) => hedge.id === hed.id
                        )?.sell_or_buy;
                      return {
                        ...hed,
                        sell_or_buy:
                          hed.sell_or_buy !== hedgeSellOrBuy
                            ? hedgeSellOrBuy
                            : hed.sell_or_buy === "buy"
                            ? "sell"
                            : "buy",
                      };
                    }),
                  };
                }),
              },
            };
          });
        }
      })
      .catch((error) => {
        setIsTradeRequestFire(null);
        console.log("error > ", error);
      })
      .finally(() => {
        setIsLoading(false);
        setIsRequestActive(false);
      });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedLegsChange = useCallback(
    debounce((type, value, id, oldValue) => {
      const tradesData = [];
      tradeData?.data?.trades?.forEach((item) => {
        tradesData.push({
          column_id: item.column_id ?? item.id,
          quantity_t: item.quantity_t.value,
        });
      });
      const prepareBody = {
        [BACKEND_KEYS_NAME.IS_SELL]: isSellActive ? 1 : 0,
        [BACKEND_KEYS_NAME.LEGS]: [
          {
            [type]: value,
            [BACKEND_KEYS_NAME.ID]: id,
          },
        ],
        ...((props.isTeamRun || props.isPricer) && {
          [BACKEND_KEYS_NAME.TEM_SUMMARY_ID]: tradeData?.data?.temp_summary_id,
        }),
        trades: tradesData,
      };
      setIsRequestActive(true);
      setIsLegPremiumRequestActive(true);
      updateTradeLegs(prepareBody)
        .then((res) => {
          setIsReverse(true);
          const data = res?.data.data;
          setTradeData((prev) => {
            return {
              ...prev,
              data: {
                ...prev.data,
                traded_premium: data?.traded_premium,
                traded_vol: data?.traded_vol,
                // trades: data?.trades ?? prev.data?.trades,
                trades: prev.data.trades.map((item) => {
                  return {
                    ...item,
                    quantity_t: {
                      ...item.quantity_t,
                      value: data?.trades?.find(
                        (trade) => trade.column_id === item.column_id
                      )?.quantity_t.value,
                    },
                    // hedges: item.hedges.map((hedge) => {
                    //   return {
                    //     ...hedge,
                    //     delta_quantity_t: {
                    //       ...hedge.delta_quantity_t,
                    //       value: data?.trades
                    //         ?.find(
                    //           (trade) => trade.column_id === item.column_id
                    //         )
                    //         ?.hedges?.find((el) => el.id === hedge.id)
                    //         ?.delta_quantity_t?.value,
                    //     },
                    //     delta: {
                    //       ...hedge.delta,
                    //       value: data?.trades
                    //         ?.find(
                    //           (trade) => trade.column_id === item.column_id
                    //         )
                    //         ?.hedges?.find((el) => el.id === hedge.id)?.delta
                    //         ?.value,
                    //     },
                    //   };
                    // }),
                    hedges: data?.trades?.find(
                      (trade) => trade.column_id === item.column_id
                    )?.hedges,
                    legs: item.legs.map((leg) => {
                      return {
                        ...leg,
                        quantity_t: {
                          ...leg.quantity_t,
                          value: data?.trades
                            ?.find(
                              (trade) => trade.column_id === item.column_id
                            )
                            ?.legs?.find((el) => el.id === leg.id)?.quantity_t
                            ?.value,
                        },
                      };
                    }),
                  };
                }),
                left: {
                  exchange_placeholder: prev.data.left?.exchange_placeholder,
                  exchanges: prev.data.left?.exchanges,
                  is_sell: data?.is_sell,
                  title: data?.title,
                  //update hedges except the sell_or_buy
                  hedges: data?.hedges,
                  legs: data?.legs,
                },
              },
            };
          });
        })
        .catch((error) => {
          // Revert to old value on error
          setTradeData((prev) => {
            return {
              ...prev,
              data: {
                ...prev.data,
                left: {
                  ...prev.data.left,
                  legs: prev.data.left.legs.map((item) => {
                    if (item.id !== id) return item;
                    if (type === "premium") {
                      item.premium = { ...item.premium, value: oldValue };
                    } else if (type === "vol") {
                      item.volatility = { ...item.volatility, value: oldValue };
                    }
                    return item;
                  }),
                },
              },
            };
          });
          console.log("error> ", error);
        })
        .finally(() => {
          setIsRequestActive(false);
          setIsLegPremiumRequestActive(false);
        });
    }, 1000),
    [tradeData?.data?.temp_summary_id, tradeData?.data?.trades]
  );

  const handleLegsChange = (type, value, id) => {
    // Get old value before updating
    const leg = tradeData?.data?.left?.legs?.find((item) => item.id === id);
    const oldValue =
      type === "premium" ? leg?.premium.value : leg?.volatility?.value;

    // Update local state immediately
    setTradeData((prev) => {
      return {
        ...prev,
        data: {
          ...prev.data,
          left: {
            exchange_placeholder: prev.data.left?.exchange_placeholder,
            exchanges: prev.data.left?.exchanges,
            hedges: prev.data.left?.hedges,
            is_sell: prev.data.left?.is_sell,
            title: prev.data.left?.title,
            legs: prev.data.left.legs.map((item) => {
              if (item.id !== id) return item;
              if (type === "premium") {
                item.premium = { ...item.premium, value };
              } else if (type === "vol") {
                item.volatility = { ...item.volatility, value };
              }
              return item;
            }),
          },
        },
      };
    });
    // Trigger debounced API call with old value
    debouncedLegsChange(type, value, id, oldValue);
  };

  function handleTradeSummary(type, value) {
    // Store old value before making the request
    const oldValue =
      type === "premium"
        ? tradeData?.data?.traded_premium?.premium?.value
        : tradeData?.data?.traded_vol?.vol?.value;

    if (type === "premium") {
      setOldTradeData((old) => ({
        ...old,
        traded_premium: oldValue,
      }));
    } else if (type === "vol") {
      setOldTradeData((old) => ({
        ...old,
        traded_vol: oldValue,
      }));
    }

    // send array of trades
    const tradesData = [];
    tradeData?.data?.trades?.forEach((item) => {
      tradesData.push({
        column_id: item.column_id ?? item.id,
        quantity_t: item.quantity_t.value,
      });
    });
    const prepareBody = {
      is_sell: isSellActive ? 1 : 0,
      [BACKEND_KEYS_NAME.TEM_SUMMARY_ID]: tradeData.data?.temp_summary_id,
      [type]: value,
      ...((props.isTeamRun || props.isPricer) && {
        summary_id: +returnSelectedIdForEditTradeDetails(props).split("/")[1],
      }),
      trades: tradesData,
    };

    setIsRequestActive(true);
    setIsPremiumRequestActive(true);
    updateSummaryTrade(prepareBody)
      .then((res) => {
        setIsReverse(true);
        const data = res.data.data;
        setEditTradeValue(undefined);
        setEditVolatilityValue(undefined);
        setTradeData((prev) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              traded_premium: data?.traded_premium,
              traded_vol: data?.traded_vol,
              // trades: data?.trades ?? prev.data?.trades,
              trades: prev.data.trades.map((item) => {
                return {
                  ...item,
                  quantity_t: {
                    ...item.quantity_t,
                    value: data?.trades?.find(
                      (trade) => trade.column_id === item.column_id
                    )?.quantity_t.value,
                  },
                  hedges: data?.trades?.find(
                    (trade) => trade.column_id === item.column_id
                  )?.hedges,

                  // hedges: item.hedges.map((hedge) => {
                  //   return {
                  //     ...hedge,
                  //     delta_quantity_t: {
                  //       ...hedge.delta_quantity_t,
                  //       value: data?.trades
                  //         ?.find((trade) => trade.column_id === item.column_id)
                  //         ?.hedges?.find((el) => el.id === hedge.id)
                  //         ?.delta_quantity_t?.value,
                  //     },
                  //     delta: {
                  //       ...hedge.delta,
                  //       value: data?.trades
                  //         ?.find((trade) => trade.column_id === item.column_id)
                  //         ?.hedges?.find((el) => el.id === hedge.id)?.delta
                  //         ?.value,
                  //     },
                  //   };
                  // }),
                  legs: item.legs.map((leg) => {
                    return {
                      ...leg,
                      quantity_t: {
                        ...leg.quantity_t,
                        value: data?.trades
                          ?.find((trade) => trade.column_id === item.column_id)
                          ?.legs?.find((el) => el.id === leg.id)?.quantity_t
                          ?.value,
                      },
                    };
                  }),
                };
              }),
              left: {
                exchange_placeholder: prev.data.left?.exchange_placeholder,
                exchanges: prev.data.left?.exchanges,
                is_sell: data?.is_sell,
                title: data?.title,
                hedges: data?.hedges,
                legs: data?.legs,
              },
            },
          };
        });
      })
      .catch((error) => {
        // Revert to old value based on type
        if (type === "premium") {
          setEditTradeValue(oldTradeData.traded_premium);
        } else if (type === "vol") {
          setEditVolatilityValue(oldTradeData.traded_vol);
        }
        console.log("error> ", error);
      })
      .finally(() => {
        setIsRequestActive(false);
        setIsPremiumRequestActive(false);
      });
  }

  const handleLiveChange = (checked) => {
    setIsLive(checked);
    setIsLiveRequestActive(true);
    setIsRequestActive(true);

    const tradesData = [];
    tradeData?.data?.trades?.forEach((item) => {
      tradesData.push({
        column_id: item.column_id ?? item.id,
        quantity_t: item.quantity_t?.value,
      });
    });

    const prepareBody = {
      [BACKEND_KEYS_NAME.TEM_SUMMARY_ID]: tradeData?.data?.temp_summary_id,
      [BACKEND_KEYS_NAME.IS_SELL]: isSellActive ? 1 : 0,
      live: checked ? 1 : 0,
      trades: tradesData,
    };

    updateLiveTrade(prepareBody)
      .then((res) => {
        setIsReverse(true);
        const data = res?.data?.data;
        setIsLive(Boolean(data?.[BACKEND_KEYS_NAME.IS_LIVE]));
        handleCopyTradeDetails(data?.copy_shorthand);
        setTradeData((prev) => {
          return {
            ...prev,
            data: {
              ...prev.data,
              [BACKEND_KEYS_NAME.IS_LIVE]:
                data?.[BACKEND_KEYS_NAME.IS_LIVE] ??
                prev.data?.[BACKEND_KEYS_NAME.IS_LIVE],
              traded_premium: data?.traded_premium ?? prev.data.traded_premium,
              traded_vol: data?.traded_vol ?? prev.data.traded_vol,
              trades: prev.data.trades.map((item) => {
                const matchingTrade = data?.trades?.find(
                  (trade) => trade.column_id === item.column_id
                );
                return {
                  ...item,
                  quantity_t: {
                    ...item.quantity_t,
                    value: matchingTrade?.quantity_t?.value,
                  },
                  hedges: matchingTrade?.hedges ?? item.hedges,
                  legs: item.legs.map((leg) => {
                    return {
                      ...leg,
                      quantity_t: {
                        ...leg.quantity_t,
                        value: matchingTrade?.legs?.find(
                          (el) => el.id === leg.id
                        )?.quantity_t?.value,
                      },
                    };
                  }),
                };
              }),
              left: {
                exchange_placeholder: prev.data.left?.exchange_placeholder,
                exchanges: prev.data.left?.exchanges,
                is_sell: data?.is_sell,
                title: data?.title,
                hedges: data?.hedges,
                legs: data?.legs,
              },
            },
          };
        });
      })
      .catch(() => {
        setIsLive(!checked);
      })
      .finally(() => {
        setIsLiveRequestActive(false);
        setIsRequestActive(false);
      });
  };

  const triggerPostEditApi = () => {
    setIsCreateLoading(true);
    const prepareBody = {
      team_run_row_id:
        props.rowSelectedInfo?.[
          location.pathname.includes(ROUTES_PATH.AUDIT_TRAIL.index)
            ? BACKEND_KEYS_NAME.TEAM_RUN_ROW_ID
            : BACKEND_KEYS_NAME.ID
        ],
      temp_summary_id: tradeData.data.temp_summary_id,
      exchange_id: tradeData.data.exchange_id,
      is_sell: isSellActive ? 1 : 0,
      [BACKEND_KEYS_NAME.IS_LIVE]: isLive ? 1 : 0,
      traded_premium: tradeData.data.traded_premium.premium.value,
      traded_vol: tradeData.data.traded_vol.vol.value,
    };
    const trades = tradeData.data.trades.map((item) => {
      return {
        trading_company_id:
          tradeData.data.trades?.at(0)?.["trading_company_id"],
        trader_id: item["trader_id"],
        quantity: item.quantity_t?.value,
        hedge_rows: item.hedges.map((hedge) => {
          return {
            id: hedge.id,
            sell_or_buy: tradeData?.data?.left?.hedges?.find(
              (el) => el.id === hedge.id
            )?.sell_or_buy,
            delta_quantity_t: hedge?.delta_quantity_t?.value,
            delta_t: hedge.delta?.value,
            underlying: hedge?.underlying?.value,
            underlying_tenor: hedge?.underlying_tenor,
          };
        }),
      };
    });

    prepareBody["trades"] = trades;
    setIsRequestActive(true);
    storeOrUpdateTradeDetails(props, prepareBody)
      .then((res) => {
        handleCopyTradeDetails(res.data.data["copy_trade_details"]);
        !props.isTeamRun && !props.isPricer && setOnSaveAndCreateClicked(true);
        // Trades are saved even when a confirmation email fails; warn instead of success (A2-1551).
        const emailFailed = res.data?.data?.confirmation_email_failed_trade_ids?.length > 0;
        toast(res.data?.message, emailFailed ? "warning" : "success");
        setIsCreateLoading(false);
      })
      .catch((err) => {
        setIsCreateLoading(false);
      })
      .finally(() => {
        setIsRequestActive(false);
      });
    setDataToLocalStorage("initialTrade", {});
  };
  //Checking for validation
  const isSaveAndCreateButtonDisabled = useMemo(() => {
    const isNotEmpty = tradeData?.data?.trades?.every((item, index) => {
      return index === 0
        ? item.trading_company !== null && item.trader !== null
        : item.trader !== null;
    });
    return !(tradeData?.data?.exchange_id && isNotEmpty);
  }, [tradeData?.data?.exchange_id, tradeData?.data?.trades]);

  const handleExchangeChange = (value) => {
    setTradeData((prev) => {
      return {
        ...prev,
        data: {
          ...prev.data,
          exchange_id: value,
        },
      };
    });
  };

  //Handle the Trader change input
  const handleTraderChange = (id, value, trader_id) => {
    const trades = tradeData.data.trades.map((item) => {
      if (item.column_id === id) {
        item.trader = value;
        item["trader_id"] = trader_id;
      }
      return item;
    });
    setTradeData((prev) => {
      return {
        ...prev,
        data: {
          ...prev.data,
          trades: trades,
        },
      };
    });
    setDataToLocalStorage("initialTrade", {
      trader_id: tradeData.data.trades[0].trader_id,
      trader: tradeData.data.trades[0].trader,
    });
  };

  const handleLegsQuantityChange = (id, value) => {
    const leftLegs = tradeData.data.left?.legs?.map((item) => {
      item.volatility.value = value;
      return item;
    });
    setTradeData((prev) => {
      return {
        ...prev,
        data: {
          ...prev.data,
          left: {
            exchange_placeholder: prev.data.left?.exchange_placeholder,
            exchanges: prev.data.left?.exchanges,
            hedges: prev.data.left?.hedges,
            is_sell: prev.data.left?.is_sell,
            title: prev.data.left?.title,
            legs: leftLegs,
          },
        },
      };
    });
  };

  function onAddTrader() {
    setIsAddTraderOpen(true);
  }
  // function onAddCompany() {
  //   setIsAddCompanyOpen(true);
  // }

  const isRequestsActive = useMemo(() => {
    return (
      isLegPremiumRequestActive ||
      isPremiumRequestActive ||
      isQuantityRequestActive ||
      isDeltaRequestActive ||
      isLiveRequestActive ||
      isCreateLoading
    );
  }, [
    isLegPremiumRequestActive,
    isPremiumRequestActive,
    isQuantityRequestActive,
    isDeltaRequestActive,
    isLiveRequestActive,
    isCreateLoading,
  ]);

  return (
    <Modal
      title={draggableTitle}
      modalRender={modalRender}
      className={dark_Theme && "content_dark ant-modal-dark"}
      open={props.isOpen}
      bodyStyle={{ overflowX: "auto", margin: 16 }}
      footer={EditTradeFooter(
        props,
        triggerPostEditApi,
        isRequestActive,
        isCreateLoading,
        isSaveAndCreateButtonDisabled,
        onAddTrader
        // onAddCompany
      )}
      width={"80vw"}
      onCancel={() => {
        setDataToLocalStorage("initialTrade", {});
        props.handleMenuItemClicked(false);
      }}
    >
      <div className="edit-trade-details">
        {/* Show Sell And Buy Button */}
        <Row gutter={[0, 15]}>
          <BuyOrSellButtons
            isLoading={isLoading || isRequestsActive}
            isSellActive={isSellActive}
            setIsSellActive={setIsSellActive}
            setIsTradeRequestFire={setIsTradeRequestFire}
          />
          {isLoading ? (
            <PreLoadPagesSpin
              className={" edit-trade-loading"}
              dark_Theme={dark_Theme}
            />
          ) : (
            <>
              {/* Show Traded premium */}
              <Row style={{ alignItems: "center", width: "100%" }}>
                <Col span={6} md={8} lg={5} className="mt-2">
                  <strong className="title">
                    {tradeData?.data?.left?.title || "API2 Feb-23:Put 0.000"}
                  </strong>
                </Col>
                <Col className="mt-2" style={{ marginRight: 16 }}>
                  <Checkbox
                    checked={isLive}
                    className="live-checkbox"
                    disabled={
                      isTradeRequestFire === null ||
                      isRequestsActive 
                    }
                    onChange={(e) => handleLiveChange(e.target.checked)}
                  >
                    Live
                  </Checkbox>
                </Col>
                <Col span={12} md={16} lg={12}>
                  <EditTradePremium
                    isTradeRequestFire={isTradeRequestFire}
                    tradedPremium={tradeData?.data?.traded_premium}
                    tradedVolatility={tradeData?.data?.traded_vol}
                    disabled={isRequestsActive}
                    editTradeValue={editTradeValue}
                    setEditTradeValue={setEditTradeValue}
                    editVolatilityValue={editVolatilityValue}
                    setEditVolatilityValue={setEditVolatilityValue}
                    setIsRequestActive={setIsRequestActive}
                  />
                </Col>
              </Row>

              {/* End Of Show Sell And Buy */}
              <div className="d-flex mt-5">
                <div
                  className="left-trade"
                  style={{
                    marginTop: "5px",
                    width: "870px",
                  }}
                >
                  {/* for alignment */}
                  <Col className="opacity-0" span={18}>
                    .
                  </Col>
                  <Col className="opacity-0" span={18}>
                    .
                  </Col>
                  <Col className="opacity-0" span={18}>
                    .
                  </Col>
                  <Col className="opacity-0" span={18}>
                    .
                  </Col>
                  {tradeData?.data?.left?.legs?.map((leg, index) => {
                    return (
                      <div className="d-flex flex-column gap-3">
                        <Row gutter={10}>
                          <Col span={19}></Col>
                          <Col span={2} className="ms-3">
                            {index === 0 && (
                              <span>
                                {leg["sell_or_buy"] && (
                                  <span className="sell-or-buy--label">
                                    {leg["sell_or_buy"]}
                                  </span>
                                )}
                              </span>
                            )}
                          </Col>
                          <Col span={2}>
                            {index === 0 && (
                              <Select
                                disabled={
                                  isTradeRequestFire === null ||
                                  isRequestsActive
                                }
                                placeholder={
                                  tradeData?.data?.left?.[
                                    API_KEYS.EXCHANGE_PLACEHOLDER
                                  ]
                                }
                                key={leg[STATE_KEYS_NAME.EXCHANGE]}
                                popupClassName={`${
                                  dark_Theme && "dropdown-dark"
                                } exchange-select`}
                                className="audit-drop-down exchange-select"
                                defaultValue={tradeData.data.exchange_id}
                                onChange={(value) =>
                                  handleExchangeChange(value)
                                }
                                options={exChangeData}
                                value={tradeData.data.exchange_id}
                                // style={{ minWidth: 95, maxWidth: 95 }}
                              />
                            )}
                          </Col>
                        </Row>
                        <Col span={24}>
                          <Row gutter={10}>
                            <Col span={8} className="me-1">
                              <span>{leg.label}</span>
                            </Col>
                            <Col span={5}>
                              <InputNumber
                                onStep={() => setIsRequestActive(true)}
                                onInput={() => setIsRequestActive(true)}
                                onFocus={() => setIsRequestActive(true)}
                                onBlur={() => setIsRequestActive(false)}
                                placeholder={
                                  leg?.[API_KEYS.QUANTIZE_PLACEHOLDER]
                                }
                                disabled={
                                  isTradeRequestFire === null ||
                                  isRequestsActive ||
                                  leg?.[BACKEND_KEYS_NAME.VOLATILITY]?.[
                                    BACKEND_KEYS_NAME.IS_DISABLED
                                  ] === 1
                                }
                                value={
                                  leg?.[BACKEND_KEYS_NAME.VOLATILITY]?.value
                                }
                                step={
                                  leg?.[BACKEND_KEYS_NAME.VOLATILITY]
                                    ?.step_size || 0.005
                                }
                                onChange={(value) => {
                                  handleLegsChange("vol", value, leg.id);
                                }}
                                addonAfter={
                                  leg?.[BACKEND_KEYS_NAME.VOLATILITY]?.symbol
                                }
                              />
                            </Col>
                            <Col span={5}>
                              <InputNumber
                                onStep={() => setIsRequestActive(true)}
                                onInput={() => setIsRequestActive(true)}
                                onFocus={() => setIsRequestActive(true)}
                                onBlur={() => setIsRequestActive(false)}
                                type="number"
                                placeholder={
                                  leg?.[API_KEYS.QUANTIZE_PLACEHOLDER]
                                }
                                disabled={
                                  isTradeRequestFire === null ||
                                  isRequestsActive ||
                                  leg?.[BACKEND_KEYS_NAME.PREMIUM]
                                    ?.is_disabled === 1
                                }
                                defaultValue={
                                  leg?.[BACKEND_KEYS_NAME.PREMIUM]?.[
                                    BACKEND_KEYS_NAME.VALUE
                                  ]
                                }
                                value={
                                  leg?.[BACKEND_KEYS_NAME.PREMIUM]?.[
                                    BACKEND_KEYS_NAME.VALUE
                                  ]
                                }
                                step={
                                  leg?.[BACKEND_KEYS_NAME.STEP_SIZE] || 0.005
                                }
                                onChange={(value) => {
                                  handleLegsChange("premium", value, leg.id);
                                }}
                                style={{ width: "100%" }}
                              />
                            </Col>
                            <Col span={2} className="ms-5">
                              <span className="m-0">
                                {leg["sell_or_buy"] && (
                                  <span className="sell-or-buy--label">
                                    {leg["sell_or_buy"]}
                                  </span>
                                )}
                              </span>
                            </Col>
                          </Row>
                        </Col>
                      </div>
                    );
                  })}
                  <Col span={24} style={{ marginTop: "20px" }}>
                    {tradeData?.data?.left?.hedges.map((hedge, hedgeIndex) => (
                      <Row gutter={10}>
                        {hedgeIndex !== 0 && (
                          <>
                            <Col className="opacity-0" span={20}>
                              y
                            </Col>
                            <Col className="opacity-0" span={20}>
                              y
                            </Col>
                          </>
                        )}
                        <Col span={8} className="me-1">
                          <span>{hedge?.label}</span>
                        </Col>
                        <Col span={5}></Col>
                        <Col span={5}>
                          <p
                            style={{ textAlign: "start" }}
                            className="edit-trade-underlying m-0"
                          >
                            {
                              hedge[BACKEND_KEYS_NAME.UNDERLYING]?.[
                                BACKEND_KEYS_NAME.VALUE
                              ]
                            }
                          </p>
                        </Col>
                        <Col span={2} className="ms-5">
                          {hedge["sell_or_buy"] && (
                            <span className="sell-or-buy--label">
                              {hedge["sell_or_buy"]}
                            </span>
                          )}
                        </Col>
                      </Row>
                    ))}
                  </Col>
                </div>
                <div className="right-trade d-flex  mx-5">
                  {tradeData?.data?.trades?.map((col, colIndex) => {
                    return (
                      <TradeColItem
                        key={col.column_id ?? colIndex}
                        col={col}
                        isReverse={isReverse}
                        setIsReverse={setIsReverse}
                        props={props}
                        tradeData={tradeData}
                        colIndex={colIndex}
                        tradingCompanyId={tradingCompanyId}
                        setTradingCompanyId={setTradingCompanyId}
                        handleTraderChange={handleTraderChange}
                        editQuantityValue={editQuantityValue}
                        isTradeRequestFire={isTradeRequestFire}
                        setIsRequestActive={setIsRequestActive}
                        handleLegsQuantityChange={handleLegsQuantityChange}
                        setTradeData={setTradeData}
                        isSellActive={isSellActive}
                        isRequestActive={isRequestActive}
                        currentCol={currentCol}
                        isRequestsActive={isRequestsActive}
                        setIsQuantityRequestActive={setIsQuantityRequestActive}
                        setIsDeltaRequestActive={setIsDeltaRequestActive}
                        setCurrentCol={setCurrentCol}
                        oldTradeData={oldTradeData}
                        setOldTradeData={setOldTradeData}
                      />
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </Row>
      </div>
      {isAddTraderOpen && (
        <AddTraderForm
          setTradingCompanyId={setTradingCompanyId}
          isOpen={isAddTraderOpen}
          setIsOpen={setIsAddTraderOpen}
          TradingCompanyId={tradingCompanyId}
          setTraderId={() => {}}
          onTraderChange={handleTraderChange}
          productId={props.rowSelectedInfo?.["product_id"]}
        />
      )}
      {/* {isAddCompanyOpen && (
        <AddCompanyForm
          isOpen={isAddCompanyOpen}
          setIsOpen={setIsAddCompanyOpen}
        />
      )} */}
    </Modal>
  );
};
export default EditTradeDetails;
